import { useEffect } from "react";
import { AlertTriangle, X } from "lucide-react";

export default function ConfirmDialog({
  open,
  title = "Sicher?",
  body = "",
  confirmLabel = "Bestätigen",
  cancelLabel = "Abbrechen",
  destructive = false,
  confirmDisabled = false,
  confirmTestId = "confirm-dialog-confirm",
  modalTestId = "confirm-dialog",
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/75 backdrop-blur-sm"
      onClick={onCancel}
      data-testid="confirm-dialog-overlay"
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-[#17171a] border border-[#2a2a30] p-6 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        data-testid={modalTestId}
      >
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 inline-flex items-center justify-center w-9 h-9 rounded-md text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
          aria-label="Schließen"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3">
          <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0 ${destructive ? "bg-red-500/15 text-red-300" : "bg-[#1f1f23] text-[#9a9aa3]"}`}>
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold" data-testid="confirm-dialog-title">{title}</h2>
            <p className="text-sm text-[#9a9aa3] mt-1.5 leading-relaxed" data-testid="confirm-dialog-body">
              {body}
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="inline-flex items-center justify-center h-10 px-4 rounded-xl border border-[#2a2a30] text-white font-medium hover:bg-[#1f1f23] transition-colors"
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`inline-flex items-center justify-center h-10 px-5 rounded-xl font-semibold transition-colors ${
              destructive
                ? "bg-red-500 hover:bg-red-600 text-white"
                : "bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white"
            }`}
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
